import type { ApiDifficulty, ProblemSummaryResponse } from '@/api/types';
import { primaryTopic } from '@/data/topics';

const LEVEL: Record<ApiDifficulty, number> = { EASY: 0, MEDIUM: 1, HARD: 2 };

interface RelatedSource {
  slug: string;
  difficulty: ApiDifficulty;
  topics: string[];
}

/**
 * "O'xshash masalalar": avvalo asosiy mavzudagi (primaryTopic — breadcrumb va mavzu
 * havolasi bilan bir xil), keyin boshqa umumiy mavzudagilar. Har guruh ichida:
 * keyingi qiyinlik → shu qiyinlik → qolganlari; teng bo'lsa id bo'yicha. Prerender va client bir xil
 * natija berishi uchun sof funksiya.
 */
export function pickRelated(
  current: RelatedSource,
  candidates: ProblemSummaryResponse[],
  limit = 5,
): ProblemSummaryResponse[] {
  const topics = new Set(current.topics);
  if (topics.size === 0) return [];
  const primary = primaryTopic(current.topics)?.slug;
  const level = LEVEL[current.difficulty];
  const target = Math.min(level + 1, LEVEL.HARD);

  const levelRank = (d: ApiDifficulty) => {
    const l = LEVEL[d];
    if (l === target) return 0;
    if (l === level) return 1;
    return l > target ? 2 : 3;
  };

  return candidates
    .filter((p) => p.slug !== current.slug && (p.topics ?? []).some((t) => topics.has(t)))
    .map((p) => ({
      p,
      rank: levelRank(p.difficulty),
      primary: primary && (p.topics ?? []).includes(primary) ? 0 : 1,
    }))
    .sort((a, b) => a.primary - b.primary || a.rank - b.rank || a.p.id - b.p.id)
    .slice(0, limit)
    .map((x) => x.p);
}

/** Oson → o'rta → qiyin, keyin id bo'yicha (mavzu sahifasi tartibi). */
export function sortByDifficulty(list: ProblemSummaryResponse[]): ProblemSummaryResponse[] {
  return [...list].sort((a, b) => LEVEL[a.difficulty] - LEVEL[b.difficulty] || a.id - b.id);
}
